import React from 'react';
import { AlertCircle, RefreshCcw, Vote } from 'lucide-react';
import { VoteDetails } from './ProposalDiff';
import { findCircle, formatDate } from './governance';

const VotesPanel = ({ votes, votesLoading, votesError, circles, onRefreshVotes, onDecision }) => {
  const [pendingVoteId, setPendingVoteId] = React.useState(null);
  const [confirmRejectId, setConfirmRejectId] = React.useState(null);

  const decide = async (voteId, decision) => {
    if (pendingVoteId) return;
    setPendingVoteId(voteId);
    setConfirmRejectId(null);
    try {
      await onDecision(voteId, decision);
    } finally {
      setPendingVoteId(null);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-indigo-700 font-semibold">
            <Vote size={16} /> Votes en cours (hors réunion)
          </div>
          <h2 className="text-xl font-bold text-slate-900">Mes votes à effectuer</h2>
          <p className="text-slate-500 text-sm">
            Accepte ou refuse les propositions. Il suffit d'un refus pour qu'une proposition retourne à son auteur ;
            sans réponse dans le délai, ton vote compte comme un accord.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRefreshVotes}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
            title="Actualiser mes votes"
            aria-label="Actualiser mes votes"
          >
            <RefreshCcw size={14} />
          </button>
        </div>
      </div>

      {votesError && (
        <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-xl">
          <AlertCircle size={16} />
          {votesError}
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-3">
        {votesLoading && votes.length === 0 ? (
          <div className="text-center text-slate-400 text-sm">Chargement…</div>
        ) : votes.length === 0 ? (
          <div className="text-center text-slate-400 text-sm">Aucun vote en attente.</div>
        ) : (
          votes.map((vote) => {
            const circle = findCircle(circles, vote.circle_id);
            const autoAccept = formatDate(vote.auto_accept_at);
            const busy = String(pendingVoteId) === String(vote.id);
            const confirming = String(confirmRejectId) === String(vote.id);
            return (
              <div key={vote.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-semibold text-slate-900">{vote.tension_title || 'Proposition'}</p>
                    <p className="text-xs text-slate-500">
                      {circle?.name ? <>Cercle concerné : <span className="font-medium text-slate-700">{circle.name}</span></> : null}
                      {circle?.name && vote.author_name ? ' · ' : null}
                      {vote.author_name ? <>Proposée par {vote.author_name}</> : null}
                    </p>
                    {vote.tension_summary && (
                      <p className="text-xs text-slate-600">
                        <span className="font-semibold text-slate-700">Ce qui coince :</span> {vote.tension_summary}
                      </p>
                    )}
                    {autoAccept && (
                      <p className="text-xs text-slate-500">Sans réponse, accepté automatiquement le {autoAccept}.</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 md:justify-end shrink-0">
                    {confirming ? (
                      <>
                        <span className="w-full text-[11px] text-red-700 md:text-right">
                          Refuser renvoie la proposition en brouillon à son auteur.
                        </span>
                        <button
                          type="button"
                          onClick={() => setConfirmRejectId(null)}
                          className="px-3 py-1.5 text-xs border border-slate-200 bg-white text-slate-600 rounded-lg hover:bg-slate-50"
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          onClick={() => decide(vote.id, 'reject')}
                          disabled={Boolean(pendingVoteId)}
                          className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          Confirmer le refus
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setConfirmRejectId(vote.id)}
                          disabled={Boolean(pendingVoteId)}
                          className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50"
                        >
                          Refuser
                        </button>
                        <button
                          type="button"
                          onClick={() => decide(vote.id, 'accept')}
                          disabled={Boolean(pendingVoteId)}
                          className="px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                        >
                          {busy ? 'Envoi…' : 'Accepter'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <VoteDetails vote={vote} circles={circles} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VotesPanel;
